<script>
import Layout from "@/router/layouts/main";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/state/services";
import { voucherStatus, formatPoints, formatDate, formatPlace } from "@/state/helpers/experiences";

// Voucher: estado da compra; o código de retirada só aparece em approved/completed
export default {
  components: { Layout, Breadcrumb },
  data() {
    return {
      loading: true,
      errored: false,
      empty: false,
      voucher: null,
    };
  },
  computed: {
    status() {
      return this.voucher ? voucherStatus(this.voucher.status) : null;
    },
    hasCode() {
      return this.voucher && ["approved", "completed"].includes(this.voucher.status) && !!this.voucher.code;
    },
    isRefunded() {
      return this.voucher && ["rejected", "canceled"].includes(this.voucher.status);
    },
    partner() {
      return (this.voucher && this.voucher.experience.partner) || {};
    },
  },
  methods: {
    formatPoints,
    formatDate,
    formatPlace,
    load() {
      this.loading = true;
      this.errored = false;
      this.empty = false;

      api
        .get("experiences/vouchers/" + encodeURIComponent(this.$route.params.protocol))
        .then((response) => {
          if (response.data.status == "success") {
            this.voucher = response.data.voucher;
          } else {
            this.empty = true;
          }
        })
        .catch(() => {
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    copied() {
      this.$toast.success("Código copiado.");
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <Breadcrumb :items="[{ text: 'Meus resgates', to: '/experiences/vouchers' }, { text: $route.params.protocol }]" />

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="default" role="status"></b-spinner>
    </div>
    <div v-else-if="errored" class="text-center py-5">
      <p>Pedimos desculpas, não podemos recuperar as informações neste momento.</p>
      <button class="btn btn-default" v-on:click="load()">Tentar novamente</button>
    </div>
    <div v-else-if="empty" class="ds-page">
      <div class="ds-box ds-result text-center">
        <i class="bx bx-error-circle ds-result-icon" aria-hidden="true"></i>
        <h4>Resgate não encontrado</h4>
        <router-link tag="a" to="/experiences/vouchers" class="btn btn-default btn-lg btn-block text-uppercase mt-4">Meus resgates</router-link>
      </div>
    </div>
    <div v-else-if="voucher" class="ds-page">
      <div class="ds-box text-center">
        <i :class="[status.icon, 'ds-result-icon']" aria-hidden="true"></i>
        <span :class="'badge badge-' + status.variant + ' font-size-12 text-uppercase mb-2'">{{ status.label }}</span>
        <h4 class="mb-1">{{ voucher.experience.title }}</h4>
        <p class="text-muted mb-0">Protocolo {{ voucher.protocol }}</p>
      </div>

      <div class="ds-box ds-summary">
        <dl>
          <div>
            <dt>Parceiro</dt>
            <dd>{{ partner.name }}</dd>
          </div>
          <div>
            <dt>Pontos</dt>
            <dd>{{ formatPoints(voucher.points) }}</dd>
          </div>
          <div>
            <dt>Data do resgate</dt>
            <dd>{{ formatDate(voucher.date, true) }}</dd>
          </div>
          <div v-if="voucher.status == 'completed' && voucher.dateCompleted">
            <dt>Concluída em</dt>
            <dd>{{ formatDate(voucher.dateCompleted, true) }}</dd>
          </div>
        </dl>
      </div>

      <div v-if="voucher.status == 'pending'" class="ds-box">
        <div class="ds-note">
          <i class="bx bx-time-five" aria-hidden="true"></i>
          <span>Recebemos seu resgate. Assim que a equipe aprovar, o código de retirada aparece aqui e enviamos por e-mail.</span>
        </div>
      </div>

      <template v-if="hasCode">
        <div class="ds-box text-center">
          <h5 class="mb-0">Código de retirada</h5>
          <span class="ex-code notranslate">{{ voucher.code }}</span>
          <button class="btn btn-default text-uppercase" v-clipboard:copy="voucher.code" v-clipboard:success="copied">
            <i class="bx bx-copy align-middle"></i> Copiar código
          </button>
          <p class="text-muted mt-3 mb-0">Apresente este código ao parceiro.</p>
        </div>
        <div class="ds-box">
          <h5 class="mb-3">Onde utilizar</h5>
          <p class="mb-1"><strong>{{ partner.name }}</strong></p>
          <p class="text-muted mb-2">{{ [partner.address, formatPlace(partner)].filter(Boolean).join(' · ') }}</p>
          <a v-if="partner.mapUrl" :href="partner.mapUrl" target="_blank" rel="noopener" class="btn btn-outline-default btn-sm">
            Como chegar <i class="bx bx-link-external align-middle"></i>
          </a>
        </div>
        <div v-if="voucher.experience.rules" class="ds-box">
          <h5 class="mb-3">Observações e regras</h5>
          <div class="ex-content" v-html="voucher.experience.rules"></div>
        </div>
      </template>

      <div v-if="isRefunded" class="ds-box">
        <p v-if="voucher.reason" class="mb-3"><strong>Motivo:</strong> {{ voucher.reason }}</p>
        <div class="ds-note">
          <i class="bx bx-revision" aria-hidden="true"></i>
          <span>Os {{ formatPoints(voucher.points) }} foram devolvidos ao seu saldo.</span>
        </div>
      </div>

      <div class="ds-actions">
        <router-link tag="a" to="/experiences/vouchers" class="btn btn-default btn-lg btn-block text-uppercase">Meus resgates</router-link>
        <router-link tag="a" to="/experiences" class="btn btn-outline-default btn-lg btn-block text-uppercase mt-2">Ver mais experiências</router-link>
      </div>
    </div>
  </Layout>
</template>
